import moment from "moment";
import { useState } from "react";
import { toast } from "sonner";
import MainLayout from "@/components/layouts";
import { useIndividualPaymentDues } from "./payment-dues.hooks";
import IndividualPaymentDuesTable from "./components/IndividualPaymentDuesTable";
import StripePaymentModal from "@/components/stripe/StripePaymentModal";
import PaymentMethodSelectionDialog from "@/components/stripe/PaymentMethodSelectionDialog";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  createStripePaymentForIndividualDue,
  finalizeStripePaymentForIndividualDue,
  getIndividualPaymentDueReport,
  markIndividualPaymentDuesAsPaid,
} from "./payment-dues.api";
import { IndividualPaymentDue } from "./payment-dues.entity";
import { PaymentDueReport } from "@/app/vendors/payment-dues/payment-dues.entity";
import type { CreatePaymentSheetResponse, StripePaymentMethodType } from "@/services/stripe-subs-payments.service";
import { showStripeDueFinalizeToast } from "@/app/payment-dues/stripe-finalize-toast";

type StripeModalState = {
  isOpen: boolean;
  dueId: string | null;
  paymentIntentId: string | null;
  clientSecret: string | null;
  amount: number;
  currency: string | null;
  applicationFeeAmount: number;
  netTransferAmount: number;
  paymentMethodType: StripePaymentMethodType | null;
};

export default function PaymentDuesPage() {
  const { individualPaymentDues, loading, error, refresh } = useIndividualPaymentDues();

  const [markingKey, setMarkingKey] = useState<string | null>(null);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [stripePayingKey, setStripePayingKey] = useState<string | null>(null);
  const [creatingStripePayment, setCreatingStripePayment] = useState(false);
  const [pendingStripeDue, setPendingStripeDue] = useState<IndividualPaymentDue | null>(null);
  const initialStripeModalState: StripeModalState = {
    isOpen: false,
    dueId: null,
    paymentIntentId: null,
    clientSecret: null,
    amount: 0,
    currency: "usd",
    applicationFeeAmount: 0,
    netTransferAmount: 0,
    paymentMethodType: null,
  };
  const [stripeModalState, setStripeModalState] = useState<StripeModalState>(initialStripeModalState);

  const closeStripeModal = () => {
    setStripeModalState(initialStripeModalState);
    setStripePayingKey(null);
  };

  const closePaymentMethodDialog = () => {
    setPendingStripeDue(null);
    setStripePayingKey(null);
    setCreatingStripePayment(false);
  };

  const openStripeModal = (response: CreatePaymentSheetResponse, dueId: string) => {
    setStripeModalState({
      isOpen: true,
      dueId,
      paymentIntentId: response.paymentIntentId,
      clientSecret: response.clientSecret,
      amount: response.amount,
      currency: response.currency,
      applicationFeeAmount: response.applicationFeeAmount,
      netTransferAmount: response.netTransferAmount,
      paymentMethodType: response.paymentMethodType,
    });
  };

  const sanitizeFileName = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const buildReportFile = (report: PaymentDueReport, filename: string) => {
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
    const header = ["Type", "Particulars", "Detail Link", "Amount"];
    const rows = report.items.map((item) => {
      const typeLabel = item.type === "appointment" ? "Appointment" : "Daily Work";
      const detailLink = baseUrl ? `${baseUrl}${item.detailPath}` : item.detailPath;
      return [typeLabel, item.label, detailLink, item.amount.toFixed(2)];
    });
    const totalRow = ["", "", "Total", report.totalAmount.toFixed(2)];
    const content = [header, ...rows, totalRow].map((line) => line.join("\t")).join("\n");

    const blob = new Blob([content], { type: "text/tab-separated-values;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleMarkAsPaid = async (due: IndividualPaymentDue) => {
    const key = `individual:${due.id}`;
    setMarkingKey(key);
    try {
      await markIndividualPaymentDuesAsPaid(due.id);
      toast.success(`Marked all dues for ${due.name} as paid.`);
      await refresh();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to mark payment dues as paid.");
    } finally {
      setMarkingKey((current) => (current === key ? null : current));
    }
  };

  const handleDownloadReport = async (due: IndividualPaymentDue) => {
    const key = `individual:${due.id}`;
    setDownloadingKey(key);
    try {
      const report = await getIndividualPaymentDueReport(due.id);
      if (report.items.length === 0) {
        toast.info(`No outstanding payments to report for ${due.name}.`);
      } else {
        const slug = sanitizeFileName(due.name || "individual");
        const datePart = moment().format("YYYY-MM-DD");
        const filename = `individual-payment-report-${slug}-${datePart}.tsv`;
        buildReportFile(report, filename);
        toast.success("Individual payment report downloaded.");
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to download payment report.");
    } finally {
      setDownloadingKey((current) => (current === key ? null : current));
    }
  };

  const handleStripePayment = async (due: IndividualPaymentDue) => {
    const key = `individual:${due.id}`;
    setStripePayingKey(key);
    console.log("handleStripePayment", due);
    try {
      if (!due.ready_for_payouts) {
        toast.error("This individual is not ready for payouts yet.");
        setStripePayingKey((current) => (current === key ? null : current));
        return;
      }

      const amountInCents = Math.round(due.remaining_amount * 100);

      if (!Number.isFinite(amountInCents) || amountInCents <= 0) {
        toast.error("Unable to initiate Stripe payment: invalid amount.");
        setStripePayingKey((current) => (current === key ? null : current));
        return;
      }

      setPendingStripeDue(due);
    } catch (error: any) {
      const message = error?.message ?? "Failed to initiate Stripe payment.";
      toast.error(message);
      console.error(error);
      setStripePayingKey((current) => (current === key ? null : current));
    }
  };

  const handlePaymentMethodConfirm = async (paymentMethodType: StripePaymentMethodType) => {
    if (!pendingStripeDue) return;

    const due = pendingStripeDue;
    const amountInCents = Math.round(due.remaining_amount * 100);

    try {
      setCreatingStripePayment(true);
      const response = await createStripePaymentForIndividualDue({
        amount: amountInCents,
        recipientType: "individual",
        recipientId: due.id,
        paymentMethodType,
        kind: "due",
        paymentScope: "both",
      });

      if (!response.clientSecret) {
        toast.error(
          "Stripe payment intent was created without a client secret. Please finish the payment from the Stripe dashboard."
        );
        closePaymentMethodDialog();
        return;
      }

      openStripeModal(response, due.id);
      setPendingStripeDue(null);
      setStripePayingKey(null);
      toast.success("Stripe payment intent created. Complete the payment below.");
    } catch (error: any) {
      const message = error?.message ?? "Failed to initiate Stripe payment.";
      toast.error(message);
      console.error(error);
      closePaymentMethodDialog();
    } finally {
      setCreatingStripePayment(false);
    }
  };

  const handleStripePaymentSuccess = async (paymentIntentId: string) => {
    if (!stripeModalState.dueId) {
      closeStripeModal();
      return;
    }

    try {
      setStripePayingKey(`individual:${stripeModalState.dueId}`);
      const result = await finalizeStripePaymentForIndividualDue(stripeModalState.dueId, paymentIntentId);
      showStripeDueFinalizeToast(result.status);
      if (result.status === "paid") {
        await refresh();
      }
    } catch (error: any) {
      const message = error?.message ?? "Unable to confirm Stripe payment at this time.";
      toast.error(message);
      console.error(error);
    } finally {
      closeStripeModal();
    }
  };

  return (
    <MainLayout
      pageHeader={{
        title: "Payment Dues",
        subtitle: "Track remaining payments for individuals",
        buttonText: "Refresh",
        onButtonClick: refresh,
      }}
    >
      <div className="container mx-auto py-6 space-y-6">
        {error && (
          <div className="bg-destructive/10 text-destructive p-4 rounded-md">
            <p className="font-medium">Error loading payment dues</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Individual Payment Dues</CardTitle>
            <CardDescription>Remaining payments for individuals assigned to appointments and daily work</CardDescription>
          </CardHeader>
          <CardContent>
            <IndividualPaymentDuesTable
              data={individualPaymentDues}
              loading={loading}
              onPayViaStripe={handleStripePayment}
              onMarkAsPaid={handleMarkAsPaid}
              onDownloadReport={handleDownloadReport}
              markingKey={markingKey}
              downloadingKey={downloadingKey}
              stripePayingKey={stripePayingKey}
            />
          </CardContent>
        </Card>
      </div>
      <StripePaymentModal
        open={stripeModalState.isOpen}
        clientSecret={stripeModalState.clientSecret}
        paymentIntentId={stripeModalState.paymentIntentId}
        amount={stripeModalState.amount}
        currency={stripeModalState.currency}
        applicationFeeAmount={stripeModalState.applicationFeeAmount}
        netTransferAmount={stripeModalState.netTransferAmount}
        paymentMethodType={stripeModalState.paymentMethodType}
        onCancel={closeStripeModal}
        onSuccess={handleStripePaymentSuccess}
      />
      <PaymentMethodSelectionDialog
        open={Boolean(pendingStripeDue)}
        amount={pendingStripeDue ? Math.round(pendingStripeDue.remaining_amount * 100) : 0}
        currency="usd"
        isLoading={creatingStripePayment}
        onCancel={closePaymentMethodDialog}
        onConfirm={handlePaymentMethodConfirm}
      />
    </MainLayout>
  );
}
